/* IM Buy Box — isolated, scoped, no globals */
(() => {
  const ROOT_SELECTOR = '[data-im-buy-box]';
  const CHECKOUT_SELECTOR = '[data-imbb-checkout]';
  const STICKY_SELECTOR = '[data-imbb-sticky]';
  const BOGO_RELOAD_MARKER = '_imbb_bogo_reload';

  const clampInt = (value, min = 1) => {
    const number = Math.floor(Number(value) || 0);
    return Math.max(min, number);
  };

  const slugKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  function parseKeyList(raw) {
    return String(raw || '')
      .split(/[\n,]+/g)
      .map(slugKey)
      .filter(Boolean);
  }

  function itemMatchesOption(item, optionKey) {
    const keys = parseKeyList(item && item.optionKeys);
    return !keys.length || keys.includes(slugKey(optionKey));
  }

  function normalizeParamString(rawValue) {
    if (!rawValue) return '';

    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.includes('://')) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (error) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function mergeParamStrings(baseRaw, overrideRaw) {
    const merged = new URLSearchParams(normalizeParamString(baseRaw));
    const override = new URLSearchParams(normalizeParamString(overrideRaw));

    override.forEach((value, key) => {
      if (!key) return;
      merged.set(key, value);
    });

    return merged.toString();
  }

  function isInternalParam(key) {
    return key === BOGO_RELOAD_MARKER;
  }

  function getSelectedPurchaseCard(root) {
    if (!root) return null;

    const checked = root.querySelector(
      '[data-imbb-purchase-input]:checked'
    );

    if (checked) return checked.closest('[data-imbb-purchase-card]');

    return root.querySelector('[data-imbb-purchase-card]');
  }

  function getSelectedOption(root) {
    const card = getSelectedPurchaseCard(root);

    if (!card) {
      return {
        card: null,
        key: '',
        mainMultiplier: 1,
        flavorMultiplier: 1,
        freebieMultiplier: 1,
        flavorPlanMode: 'inherit',
        flavorPlan: '',
        itemsOverride: '',
        discountCodes: '',
        bogoParams: '',
        destinationOverride: 'inherit',
        ctaText: '',
        mobileSummary: '',
        mobileCta: '',
        stickyPrice: '',
      };
    }

    return {
      card,
      key: slugKey(card.getAttribute('data-imbb-option-key')),
      mainMultiplier: clampInt(card.getAttribute('data-imbb-main-mult'), 1),
      flavorMultiplier: clampInt(
        card.getAttribute('data-imbb-flavor-mult'),
        1
      ),
      freebieMultiplier: clampInt(
        card.getAttribute('data-imbb-freebie-mult'),
        1
      ),
      flavorPlanMode:
        card.getAttribute('data-imbb-flavor-plan-mode') || 'inherit',
      flavorPlan: card.getAttribute('data-imbb-flavor-plan') || '',
      itemsOverride: card.getAttribute('data-imbb-items-override') || '',
      discountCodes:
        card.getAttribute('data-imbb-option-discounts') || '',
      bogoParams: card.getAttribute('data-imbb-option-bogo') || '',
      destinationOverride:
        card.getAttribute('data-imbb-destination-override') || 'inherit',
      ctaText: card.getAttribute('data-imbb-option-cta') || '',
      mobileSummary:
        card.getAttribute('data-imbb-option-mobile-summary') || '',
      mobileCta:
        card.getAttribute('data-imbb-option-mobile-cta') || '',
      stickyPrice:
        card.getAttribute('data-imbb-option-sticky-price') || '',
    };
  }

  function getAllConfiguredBogoKeys(root) {
    const keys = new Set();
    if (!root) return keys;

    const collect = (raw) => {
      const params = new URLSearchParams(normalizeParamString(raw));
      params.forEach((value, key) => {
        if (key && !isInternalParam(key)) keys.add(key);
      });
    };

    collect(root.getAttribute('data-imbb-bogo-params') || '');
    root.querySelectorAll('[data-imbb-option-bogo]').forEach((card) => {
      collect(card.getAttribute('data-imbb-option-bogo') || '');
    });

    return keys;
  }

  function removeInactiveBogoParams(root, effectiveParams) {
    if (!root) return;

    const configuredKeys = getAllConfiguredBogoKeys(root);
    if (!configuredKeys.size) return;

    const effective = new URLSearchParams(normalizeParamString(effectiveParams));
    const url = new URL(window.location.href);
    let changed = false;

    configuredKeys.forEach((key) => {
      if (!effective.has(key) && url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      const relative = url.pathname + (url.search || '') + url.hash;
      window.history.replaceState({}, '', relative);
    }
  }

  function getBogoConfig(root) {
    const selected = getSelectedOption(root);
    const enabled =
      !!root && root.getAttribute('data-imbb-bogo-enabled') === 'true';
    const globalParams = root
      ? root.getAttribute('data-imbb-bogo-params') || ''
      : '';

    return {
      enabled,
      params: enabled
        ? mergeParamStrings(globalParams, selected.bogoParams)
        : '',
      checkoutUrl:
        (root && root.getAttribute('data-imbb-checkout-url')) || '/checkout',
    };
  }

  function configuredParamsMatchUrl(params) {
    const incoming = new URLSearchParams(params);
    const current = new URLSearchParams(window.location.search);
    let matches = true;

    incoming.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      if (current.get(key) !== value) matches = false;
    });

    return matches;
  }

  function stripInternalParamsFromCurrentUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(BOGO_RELOAD_MARKER)) return;

    url.searchParams.delete(BOGO_RELOAD_MARKER);
    const relative = url.pathname + (url.search || '') + url.hash;
    window.history.replaceState({}, '', relative);
  }

  function mergeParamsIntoUrl(baseUrl, params) {
    const mergedUrl = new URL(baseUrl, window.location.origin);
    const incoming = new URLSearchParams(params);
    let hasAny = false;

    incoming.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      mergedUrl.searchParams.set(key, value);
      hasAny = true;
    });

    return hasAny ? mergedUrl : null;
  }

  function getBogoReloadState() {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(BOGO_RELOAD_MARKER);

    if (!raw || raw === '1') return null;

    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function restoreBogoReloadSelection(root, state) {
    if (!root || !state) return;

    const optionKey = slugKey(state.optionKey);
    if (optionKey) {
      const optionCard = Array.from(
        root.querySelectorAll('[data-imbb-purchase-card]')
      ).find(
        (card) =>
          slugKey(card.getAttribute('data-imbb-option-key')) === optionKey
      );

      if (optionCard) {
        const optionInput = optionCard.querySelector(
          '[data-imbb-purchase-input]'
        );

        if (optionInput) optionInput.checked = true;
      }
    }

    const flavorKey = String(state.flavorKey || '');
    if (flavorKey) {
      const flavorInput = Array.from(
        root.querySelectorAll('[data-imbb-flavor-input]')
      ).find((input) => input.value === flavorKey && !input.disabled);

      if (flavorInput) flavorInput.checked = true;
    }
  }

  function restoreBogoReloadScroll(state) {
    if (!state) return;

    const scrollX = Number(state.scrollX);
    const scrollY = Number(state.scrollY);

    if (!Number.isFinite(scrollX) || !Number.isFinite(scrollY)) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const html = document.documentElement;
    const previousScrollBehavior = html.style.scrollBehavior;

    html.style.scrollBehavior = 'auto';

    const restore = () => {
      window.scrollTo(
        Math.max(0, scrollX),
        Math.max(0, scrollY)
      );
    };

    restore();

    window.requestAnimationFrame(() => {
      restore();

      window.requestAnimationFrame(() => {
        restore();
      });
    });

    window.setTimeout(() => {
      restore();
      html.style.scrollBehavior = previousScrollBehavior;
    }, 120);
  }

  function reloadForBogoCheckout(root, triggerButton) {
    const config = getBogoConfig(root);
    if (!config.enabled) return false;

    const params = normalizeParamString(config.params);
    const effective = new URLSearchParams(params);
    const current = new URLSearchParams(window.location.search);
    const configuredKeys = getAllConfiguredBogoKeys(root);
    let needsReload = false;

    configuredKeys.forEach((key) => {
      const effectiveValue = effective.get(key);
      const currentValue = current.get(key);

      if (effectiveValue === null) {
        if (current.has(key)) needsReload = true;
        return;
      }

      if (currentValue !== effectiveValue) needsReload = true;
    });

    if (!needsReload) return false;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    setBusy(root, true);

    const mergedUrl = new URL(window.location.href);

    configuredKeys.forEach((key) => {
      mergedUrl.searchParams.delete(key);
    });

    effective.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      mergedUrl.searchParams.set(key, value);
    });

    const selected = getSelectedOption(root);
    const flavorInput = root.querySelector(
      '[data-imbb-flavor-input]:checked'
    );

    mergedUrl.searchParams.set(
      BOGO_RELOAD_MARKER,
      JSON.stringify({
        mode: 'checkout',
        optionKey: selected.key || '',
        flavorKey: flavorInput ? flavorInput.value : '',
        sectionId: root.getAttribute('data-imbb-section-id') || '',
        scrollX: Math.round(
          window.scrollX || window.pageXOffset || 0
        ),
        scrollY: Math.round(
          window.scrollY || window.pageYOffset || 0
        )
      })
    );

    window.location.replace(mergedUrl.toString());
    return true;
  }

  function applyBogoUrlParams(root, options = {}) {
    const config = getBogoConfig(root);
    const allowReload = !!options.allowReload;

    if (!config.enabled) return false;

    const params = normalizeParamString(config.params);
    removeInactiveBogoParams(root, params);

    if (!params) {
      stripInternalParamsFromCurrentUrl();
      return false;
    }

    if (configuredParamsMatchUrl(params)) {
      stripInternalParamsFromCurrentUrl();
      return false;
    }

    const mergedUrl = mergeParamsIntoUrl(window.location.href, params);
    if (!mergedUrl) return false;

    if (allowReload) {
      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.has(BOGO_RELOAD_MARKER)) {
        mergedUrl.searchParams.set(BOGO_RELOAD_MARKER, '1');
        window.location.replace(mergedUrl.toString());
        return true;
      }
    }

    mergedUrl.searchParams.delete(BOGO_RELOAD_MARKER);
    const relative =
      mergedUrl.pathname + (mergedUrl.search || '') + mergedUrl.hash;
    window.history.replaceState({}, '', relative);

    return false;
  }

  function extractBogoCartAttributes(root) {
    const config = getBogoConfig(root);
    if (!config.enabled) return null;

    const params = normalizeParamString(config.params);
    if (!params) return null;

    const attributes = {};
    let hasAny = false;

    new URLSearchParams(params).forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      attributes[key] = value;
      hasAny = true;
    });

    return hasAny ? attributes : null;
  }

  function copyPublicSearchParams(sourceParams, targetUrl) {
    sourceParams.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      targetUrl.searchParams.set(key, value);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, root) {
    const config = getBogoConfig(root);
    if (!config.enabled) return;

    const params = normalizeParamString(config.params);
    if (!params) return;

    new URLSearchParams(params).forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      checkoutUrl.searchParams.set(key, value);
    });
  }

  function parseDiscountCodes(raw) {
    return String(raw || '')
      .split(/[\n,]+/g)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function getDiscountCodes(root) {
    const globalCodes = parseDiscountCodes(
      root ? root.getAttribute('data-imbb-discounts') : ''
    );
    const optionCodes = parseDiscountCodes(
      getSelectedOption(root).discountCodes
    );

    return Array.from(new Set([...globalCodes, ...optionCodes]));
  }

  function getCheckoutUrl(root, discountCodes) {
    const config = getBogoConfig(root);
    const checkoutUrl = new URL(
      config.checkoutUrl || '/checkout',
      window.location.origin
    );

    copyPublicSearchParams(
      new URLSearchParams(window.location.search),
      checkoutUrl
    );

    mergeConfiguredBogoParams(checkoutUrl, root);

    if (discountCodes.length) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }

    return checkoutUrl.toString();
  }

  function getDestination(root) {
    const option = getSelectedOption(root);
    const sectionDestination =
      (root && root.getAttribute('data-imbb-destination')) || 'checkout';

    if (
      option.destinationOverride &&
      option.destinationOverride !== 'inherit'
    ) {
      return option.destinationOverride;
    }

    return sectionDestination;
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const message =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(message);
    }

    return data;
  }

  async function addItems(items) {
    const normalized = Array.isArray(items) ? items : [];
    if (!normalized.length) return null;

    if (normalized.length === 1) {
      return postJSON('/cart/add.js', normalized[0]);
    }

    return postJSON('/cart/add.js', { items: normalized });
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart (${response.status})`);
    }

    return response.json();
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      const interval = 200;
      const maxWait = 6000;
      let elapsed = 0;

      const check = async () => {
        try {
          const cart = await fetchCart();
          if (
            cart &&
            typeof cart.item_count === 'number' &&
            cart.item_count > knownItemCount
          ) {
            resolve(true);
            return;
          }
        } catch (error) {
          resolve(false);
          return;
        }

        elapsed += interval;
        if (elapsed >= maxWait) {
          resolve(false);
          return;
        }

        window.setTimeout(check, interval);
      };

      window.setTimeout(check, interval);
    });
  }

  function parseConfig(root) {
    if (!root) return { mainItems: [], freebies: [], flavors: [] };
    if (root.__imbbParsedConfig) return root.__imbbParsedConfig;

    const script = root.querySelector('script[data-imbb-config]');
    let parsed = {};

    try {
      parsed = script ? JSON.parse(script.textContent || '{}') : {};
    } catch (error) {
      console.warn('IM Buy Box: unable to parse section checkout config.', error);
      parsed = {};
    }

    root.__imbbParsedConfig = {
      mainItems: Array.isArray(parsed.mainItems) ? parsed.mainItems : [],
      freebies: Array.isArray(parsed.freebies) ? parsed.freebies : [],
      flavors: Array.isArray(parsed.flavors) ? parsed.flavors : [],
    };

    return root.__imbbParsedConfig;
  }

  function getSelectedFlavor(root) {
    const config = parseConfig(root);
    const input = root
      ? root.querySelector('[data-imbb-flavor-input]:checked')
      : null;

    if (!input) return null;
    const key = input.value;
    return config.flavors.find((flavor) => flavor.key === key) || null;
  }

  function resolveFlavorSellingPlan(flavor, option) {
    if (!flavor) return '';

    if (option.flavorPlanMode === 'none') return '';
    if (option.flavorPlanMode === 'override') {
      return String(option.flavorPlan || '').trim();
    }

    return String(flavor.sellingPlan || '').trim();
  }

  function normalizeCartItem(item) {
    if (!item) return null;

    const id = Number(item.id);
    if (!Number.isFinite(id) || id <= 0) return null;

    const normalized = {
      id,
      quantity: clampInt(item.quantity, 1),
    };

    const sellingPlan = Number(item.selling_plan || item.sellingPlan || 0);
    if (Number.isFinite(sellingPlan) && sellingPlan > 0) {
      normalized.selling_plan = sellingPlan;
    }

    if (item.properties && typeof item.properties === 'object') {
      normalized.properties = item.properties;
    }

    return normalized;
  }

  function combineDuplicateItems(items) {
    const map = new Map();

    (items || []).forEach((rawItem) => {
      const item = normalizeCartItem(rawItem);
      if (!item) return;

      const propertiesKey = item.properties
        ? JSON.stringify(item.properties)
        : '';
      const key = `${item.id}|${item.selling_plan || ''}|${propertiesKey}`;

      if (!map.has(key)) {
        map.set(key, { ...item });
      } else {
        map.get(key).quantity += item.quantity;
      }
    });

    return Array.from(map.values());
  }

  function parseItemsOverride(raw, flavor, option) {
    const text = String(raw || '').trim();
    if (!text) return null;

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      throw new Error(
        'Invalid Advanced paid items JSON on the selected purchase option.'
      );
    }

    if (!Array.isArray(parsed)) {
      throw new Error('Advanced paid items JSON must be an array.');
    }

    const flavorPlan = resolveFlavorSellingPlan(flavor, option);

    return parsed.map((item) => {
      const next = { ...(item || {}) };

      if (next.id === '__FLAVOR_VARIANT__') {
        next.id = flavor ? flavor.id : '';
      }

      if (
        next.selling_plan === '__FLAVOR_SELLING_PLAN__' ||
        next.sellingPlan === '__FLAVOR_SELLING_PLAN__'
      ) {
        next.selling_plan = flavorPlan;
        delete next.sellingPlan;
      }

      return next;
    });
  }

  function buildMainItems(root) {
    const config = parseConfig(root);
    const option = getSelectedOption(root);
    const flavor = getSelectedFlavor(root);
    const items = [];

    const override = parseItemsOverride(option.itemsOverride, flavor, option);

    if (override) {
      items.push(...override);
    } else {
      config.mainItems.forEach((item) => {
        if (!itemMatchesOption(item, option.key)) return;

        const quantity =
          clampInt(item.quantity, 1) *
          (item.multiply ? option.mainMultiplier : 1);

        items.push({
          id: item.id,
          quantity,
          selling_plan: item.sellingPlan,
        });
      });

      const includeFlavor =
        root.getAttribute('data-imbb-include-flavor') === 'true';

      if (
        includeFlavor &&
        flavor &&
        !flavor.disabled &&
        Number(flavor.id) > 0
      ) {
        items.push({
          id: flavor.id,
          quantity:
            clampInt(flavor.quantity, 1) *
            clampInt(option.flavorMultiplier, 1),
          selling_plan: resolveFlavorSellingPlan(flavor, option),
        });
      }
    }

    const additionalItemsRaw =
      option.card
        ? option.card.getAttribute('data-imbb-additional-items') || ''
        : '';

    if (additionalItemsRaw.trim()) {
      let additionalItems;

      try {
        additionalItems = JSON.parse(additionalItemsRaw);
      } catch (error) {
        throw new Error(
          'Invalid additional products configuration on the selected purchase option.'
        );
      }

      if (!Array.isArray(additionalItems)) {
        throw new Error(
          'Additional products configuration must be an array.'
        );
      }

      additionalItems.forEach((item) => {
        items.push(item);
      });
    }

    return combineDuplicateItems(items);
  }

  function buildFreebieItems(root) {
    const config = parseConfig(root);
    const option = getSelectedOption(root);
    const items = [];

    config.freebies.forEach((item) => {
      if (!itemMatchesOption(item, option.key)) return;

      const quantity =
        clampInt(item.quantity, 1) *
        (item.multiply ? option.freebieMultiplier : 1);

      items.push({ id: item.id, quantity });
    });

    return combineDuplicateItems(items);
  }

  function setStatus(root, message, isError = true) {
    const status = root && root.querySelector('[data-imbb-status]');
    if (!status) return;

    status.textContent = message || '';
    status.dataset.imbbStatusType = isError ? 'error' : 'success';
  }

  function setBusy(root, isBusy) {
    if (!root) return;

    root.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      button.disabled = !!isBusy;
      button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      button.classList.toggle('is-busy', !!isBusy);
    });
  }

  async function handleCheckoutClick(button) {
    const root = button && button.closest(ROOT_SELECTOR);
    if (!root || root.__imbbCheckoutSubmitting) return;

    if (reloadForBogoCheckout(root, button)) return;

    root.__imbbCheckoutSubmitting = true;
    setStatus(root, '');
    setBusy(root, true);

    try {
      applyBogoUrlParams(root, { allowReload: false });

      const clearCart = root.getAttribute('data-imbb-clear-cart') === '1';
      const mainItems = buildMainItems(root);
      const freebieItems = buildFreebieItems(root);
      const discountCodes = getDiscountCodes(root);
      const bogoAttributes = extractBogoCartAttributes(root);
      const destination = getDestination(root);

      if (!mainItems.length) {
        throw new Error(
          'No valid paid checkout items are configured for the selected option.'
        );
      }

      if (clearCart) {
        await postJSON('/cart/clear.js', {});
        await wait(180);
      }

      let cartBeforeAdd = null;
      try {
        cartBeforeAdd = await fetchCart();
      } catch (error) {}

      await addItems(mainItems);

      let addedQuantity = mainItems.reduce(
        (total, item) => total + clampInt(item.quantity, 1),
        0
      );

      if (freebieItems.length) {
        try {
          await addItems(freebieItems);
          addedQuantity += freebieItems.reduce(
            (total, item) => total + clampInt(item.quantity, 1),
            0
          );
        } catch (freebieError) {
          console.warn('IM Buy Box: freebies failed to add.', freebieError);
        }
      }

      let cartAfterAdd = null;
      try {
        cartAfterAdd = await fetchCart();
      } catch (error) {}

      let cartAfterAttributeUpdate = null;
      if (bogoAttributes) {
        cartAfterAttributeUpdate = await postJSON('/cart/update.js', {
          attributes: bogoAttributes,
        });
      }

      if (bogoAttributes) {
        const latestCart = cartAfterAttributeUpdate || cartAfterAdd;
        const knownItemCount =
          latestCart && typeof latestCart.item_count === 'number'
            ? latestCart.item_count
            : null;
        const baselineItemCount =
          cartBeforeAdd && typeof cartBeforeAdd.item_count === 'number'
            ? cartBeforeAdd.item_count
            : null;
        const giftAlreadyPresent =
          knownItemCount !== null &&
          baselineItemCount !== null &&
          knownItemCount > baselineItemCount + addedQuantity;

        if (!giftAlreadyPresent && knownItemCount !== null) {
          await waitForBogoGift(knownItemCount);
        }
      }

      applyBogoUrlParams(root, { allowReload: false });

      let finalCart = cartAfterAttributeUpdate || cartAfterAdd;
      try {
        finalCart = await fetchCart();
      } catch (error) {}

      document.dispatchEvent(
        new CustomEvent('im-buy-box:cart-added', {
          detail: {
            root,
            sectionId: root.getAttribute('data-imbb-section-id') || '',
            optionKey: getSelectedOption(root).key,
            flavor: getSelectedFlavor(root),
            mainItems,
            freebieItems,
            cart: finalCart,
          },
        })
      );

      if (destination === 'stay') {
        setStatus(
          root,
          root.getAttribute('data-imbb-added-message') || 'Added to cart.',
          false
        );
        setBusy(root, false);
        root.__imbbCheckoutSubmitting = false;
        return;
      }

      if (destination === 'cart') {
        window.location.href =
          root.getAttribute('data-imbb-cart-url') || '/cart';
        return;
      }

      window.location.href = getCheckoutUrl(root, discountCodes);
    } catch (error) {
      const message =
        error && error.message
          ? error.message
          : 'Unable to start checkout. Please try again.';

      setStatus(root, message, true);
      setBusy(root, false);
      root.__imbbCheckoutSubmitting = false;
    }
  }

  function ensureDefaultSelections(root) {
    if (!root) return;

    const purchaseChecked = root.querySelector(
      '[data-imbb-purchase-input]:checked'
    );
    if (!purchaseChecked) {
      const firstPurchase = root.querySelector('[data-imbb-purchase-input]');
      if (firstPurchase) firstPurchase.checked = true;
    }

    const flavorChecked = root.querySelector('[data-imbb-flavor-input]:checked');
    if (!flavorChecked) {
      const firstFlavor = Array.from(
        root.querySelectorAll('[data-imbb-flavor-input]')
      ).find((input) => !input.disabled);
      if (firstFlavor) firstFlavor.checked = true;
    }
  }

  function preloadFlavorHeroImages(root) {
    if (!root || root.__imbbFlavorHeroPreloaded) return;

    root.__imbbFlavorHeroPreloaded = true;

    const config = parseConfig(root);
    const urls = new Set();

    config.flavors.forEach((flavor) => {
      [
        flavor.heroImageSmall,
        flavor.heroImage,
        flavor.heroThumbImage,
      ].forEach((url) => {
        const normalized = String(url || '').trim();
        if (normalized) urls.add(normalized);
      });
    });

    root.__imbbFlavorHeroPreloaders = Array.from(urls).map((url) => {
      const image = new Image();

      image.decoding = 'async';
      image.src = url;

      return image;
    });
  }

  function createFlavorHeroOverlay(heroSlide) {
    const image = document.createElement('img');

    image.className =
      'imbb__galleryImage imbb__flavorHeroOverlay';

    image.setAttribute('data-imbb-flavor-hero-overlay', '');
    image.setAttribute('alt', '');
    image.setAttribute('aria-hidden', 'true');
    image.setAttribute('draggable', 'false');
    image.decoding = 'async';

    heroSlide.appendChild(image);

    return image;
  }

  function createFlavorHeroThumbOverlay(heroThumb) {
    if (!heroThumb) return null;

    const image = document.createElement('img');

    image.className =
      'imbb__thumbImg imbb__flavorHeroThumbOverlay';

    image.setAttribute('data-imbb-flavor-hero-thumb-overlay', '');
    image.setAttribute('alt', '');
    image.setAttribute('aria-hidden', 'true');
    image.setAttribute('draggable', 'false');
    image.decoding = 'async';

    heroThumb.appendChild(image);

    return image;
  }

  function getFlavorHeroState(root) {
    if (!root) return null;
    if (root.__imbbFlavorHeroState) {
      return root.__imbbFlavorHeroState;
    }

    const heroSlide = root.querySelector(
      '[data-imbb-gallery-slide][data-imbb-gallery-index="0"]'
    );

    if (!heroSlide) return null;

    /*
     * Remove any stale runtime-created layers first.
     * This keeps Shopify Theme Editor section reloads and
     * Master/Copy buy-box syncing clean.
     */
    heroSlide
      .querySelectorAll('[data-imbb-flavor-hero-overlay]')
      .forEach((image) => image.remove());

    const baseHeroImage = heroSlide.querySelector(
      '.imbb__galleryImage'
    );

    if (!baseHeroImage) return null;

    const heroThumb = root.querySelector(
      '[data-imbb-thumb][data-imbb-gallery-index="0"]'
    );

    if (heroThumb) {
      heroThumb
        .querySelectorAll('[data-imbb-flavor-hero-thumb-overlay]')
        .forEach((image) => image.remove());
    }

    const baseThumbImage = heroThumb
      ? heroThumb.querySelector('.imbb__thumbImg')
      : null;

    const overlayA = createFlavorHeroOverlay(heroSlide);
    const overlayB = createFlavorHeroOverlay(heroSlide);
    const thumbOverlay = createFlavorHeroThumbOverlay(heroThumb);

    root.__imbbFlavorHeroState = {
      baseHeroImage,
      baseThumbImage,
      heroThumb,
      overlays: [overlayA, overlayB],
      thumbOverlay,
      activeOverlayIndex: -1,
      currentKey: '__default__',
      requestId: 0,
      defaultThumbAria:
        (baseThumbImage &&
          String(baseThumbImage.getAttribute('alt') || '').trim()) ||
        (heroThumb &&
          String(heroThumb.getAttribute('aria-label') || '').trim()) ||
        '',
    };

    preloadFlavorHeroImages(root);

    return root.__imbbFlavorHeroState;
  }

  function getFlavorHeroTarget(state, flavor) {
    const heroSrc = flavor
      ? String(flavor.heroImage || '').trim()
      : '';

    if (!heroSrc) {
      return {
        key: '__default__',
        hasOverride: false,
        src: '',
        srcset: '',
        thumbSrc: '',
        alt:
          state && state.baseHeroImage
            ? String(state.baseHeroImage.getAttribute('alt') || '')
            : '',
      };
    }

    const heroSmallSrc =
      String(flavor.heroImageSmall || '').trim() || heroSrc;

    const heroThumbSrc =
      String(flavor.heroThumbImage || '').trim() ||
      heroSmallSrc;

    return {
      key: String(flavor.key || heroSrc),
      hasOverride: true,
      src: heroSrc,
      srcset: `${heroSmallSrc} 800w, ${heroSrc} 1600w`,
      thumbSrc: heroThumbSrc,
      alt: String(flavor.heroImageAlt || '').trim(),
    };
  }

  function syncFlavorHeroThumb(state, target) {
    if (!state || !state.heroThumb) return;

    const overlay = state.thumbOverlay;

    if (
      target.hasOverride &&
      overlay &&
      target.thumbSrc
    ) {
      overlay.setAttribute('src', target.thumbSrc);
      overlay.setAttribute('alt', target.alt || '');
      overlay.classList.add('is-active');

      state.heroThumb.setAttribute(
        'aria-label',
        target.alt || state.defaultThumbAria
      );

      return;
    }

    if (overlay) {
      overlay.classList.remove('is-active');
    }

    if (state.defaultThumbAria) {
      state.heroThumb.setAttribute(
        'aria-label',
        state.defaultThumbAria
      );
    } else {
      state.heroThumb.removeAttribute('aria-label');
    }
  }

  function setFlavorHeroOverlaySource(image, target) {
    if (!image || !target) return;

    if (target.srcset) {
      image.setAttribute('srcset', target.srcset);
    } else {
      image.removeAttribute('srcset');
    }

    image.setAttribute('src', target.src);
    image.setAttribute('alt', target.alt || '');
  }

  function waitForFlavorHeroImage(image) {
    if (!image) return Promise.resolve();

    const waitForLoad =
      image.complete && image.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((resolve) => {
            const finish = () => resolve();

            image.addEventListener('load', finish, { once: true });
            image.addEventListener('error', finish, { once: true });
          });

    return waitForLoad.then(() => {
      if (
        image.naturalWidth > 0 &&
        typeof image.decode === 'function'
      ) {
        return image.decode().catch(() => {});
      }

      return undefined;
    });
  }

  function syncFlavorHeroImage(root, flavor) {
    if (!root) return false;

    const state = getFlavorHeroState(root);
    if (!state) return false;

    const target = getFlavorHeroTarget(state, flavor);

    /*
     * Increment even when the requested image is already active.
     * This cancels any older image request that may still be
     * finishing after a fast sequence of Flavor clicks.
     */
    const requestId = ++state.requestId;

    syncFlavorHeroThumb(state, target);

    if (target.key === state.currentKey) {
      return target.hasOverride;
    }

    /*
     * Returning to the normal section Hero needs no new image.
     * The original Hero is permanently underneath the Flavor
     * overlays, so simply fade the overlays away.
     */
    if (!target.hasOverride) {
      state.overlays.forEach((image) => {
        image.classList.remove('is-active');
      });

      state.activeOverlayIndex = -1;
      state.currentKey = target.key;

      return false;
    }

    const nextIndex =
      state.activeOverlayIndex === 0 ? 1 : 0;

    const nextImage = state.overlays[nextIndex];

    setFlavorHeroOverlaySource(nextImage, target);

    waitForFlavorHeroImage(nextImage).then(() => {
      if (requestId !== state.requestId) return;
      if (!nextImage.naturalWidth) return;

      window.requestAnimationFrame(() => {
        if (requestId !== state.requestId) return;

        state.overlays.forEach((image, index) => {
          image.classList.toggle(
            'is-active',
            index === nextIndex
          );
        });

        state.activeOverlayIndex = nextIndex;
        state.currentKey = target.key;
      });
    });

    return true;
  }

  function syncFlavorFeaturedMainImages(root, flavor) {
    if (!root) return;

    const flavorKey = flavor
      ? String(flavor.key || '').trim()
      : '';

    root.querySelectorAll('[data-imbb-featured-main]').forEach((card) => {
      const flavorImages = Array.from(
        card.querySelectorAll(
          '[data-imbb-featured-main-flavor-image]'
        )
      );

      if (!flavorImages.length) return;

      const targetImage = flavorImages.find(
        (image) =>
          String(image.getAttribute('data-flavor-key') || '').trim() ===
          flavorKey
      ) || null;

      card.__imbbFeaturedMainRequestId =
        (card.__imbbFeaturedMainRequestId || 0) + 1;

      const requestId = card.__imbbFeaturedMainRequestId;

      const activateTarget = () => {
        if (requestId !== card.__imbbFeaturedMainRequestId) return;

        flavorImages.forEach((image) => {
          image.classList.toggle(
            'is-active',
            image === targetImage
          );
        });
      };

      if (!targetImage) {
        activateTarget();
        return;
      }

      if (targetImage.complete && targetImage.naturalWidth > 0) {
        activateTarget();
        return;
      }

      targetImage.addEventListener(
        'load',
        activateTarget,
        { once: true }
      );
    });
  }

  function syncFlavorUi(root, options = {}) {
    if (!root) return;

    const selected = root.querySelector(
      '[data-imbb-flavor-input]:checked'
    );

    root.querySelectorAll('[data-imbb-flavor-card]').forEach((card) => {
      const input = card.querySelector('[data-imbb-flavor-input]');
      const isSelected = !!input && input === selected;
      card.classList.toggle('is-selected', isSelected);
    });

    const flavor = getSelectedFlavor(root);

    syncFlavorFeaturedMainImages(root, flavor);

    const hasHeroOverride = syncFlavorHeroImage(root, flavor);

    if (!flavor || options.skipGallery) return;

    if (hasHeroOverride) {
      setGalleryIndex(root, 0);
      return;
    }

    if (Number(flavor.galleryIndex) > 0) {
      setGalleryIndex(
        root,
        Number(flavor.galleryIndex) - 1
      );
    }
  }

  function setButtonText(button, text) {
    if (!button || !text) return;
    const target = button.querySelector('[data-imbb-button-text]');
    if (target) target.textContent = text;
  }

  function syncPurchaseGalleryImages(root, optionKey) {
    if (!root) return;

    const normalizedOptionKey = slugKey(optionKey);

    root.querySelectorAll(
      '[data-imbb-purchase-gallery-group]'
    ).forEach((group) => {
      const images = Array.from(
        group.querySelectorAll(
          '[data-imbb-purchase-gallery-image]'
        )
      );

      if (!images.length) return;

      const targetImage = images.find((image) => {
        const keys = parseKeyList(
          image.getAttribute('data-option-keys')
        );

        return (
          !keys.length ||
          keys.includes(normalizedOptionKey)
        );
      }) || null;

      group.__imbbPurchaseGalleryRequestId =
        (group.__imbbPurchaseGalleryRequestId || 0) + 1;

      const requestId =
        group.__imbbPurchaseGalleryRequestId;

      const activateTarget = () => {
        if (
          requestId !==
          group.__imbbPurchaseGalleryRequestId
        ) {
          return;
        }

        images.forEach((image) => {
          const isActive = image === targetImage;

          image.style.opacity = isActive ? '1' : '0';
          image.style.zIndex = isActive ? '3' : '2';

          image.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );
        });
      };

      if (!targetImage) {
        activateTarget();
        return;
      }

      if (
        targetImage.complete &&
        targetImage.naturalWidth > 0
      ) {
        activateTarget();
        return;
      }

      targetImage.addEventListener(
        'load',
        activateTarget,
        { once: true }
      );
    });
  }

  function syncPurchaseUi(root) {
    if (!root) return;

    const selectedInput = root.querySelector(
      '[data-imbb-purchase-input]:checked'
    );

    root.querySelectorAll('[data-imbb-purchase-card]').forEach((card) => {
      const input = card.querySelector('[data-imbb-purchase-input]');
      const isSelected = !!input && input === selectedInput;
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    const option = getSelectedOption(root);

    syncPurchaseGalleryImages(root, option.key);

    const mainCta = root.querySelector('[data-imbb-main-cta]');
    const floatingSummary = root.querySelector('[data-imbb-floating-summary]');
    const floatingSub = root.querySelector('[data-imbb-floating-sub]');
    const floatingCta = root.querySelector('[data-imbb-floating-cta]');

    if (mainCta) {
      const fallback = mainCta.getAttribute('data-imbb-default-label') || '';
      setButtonText(mainCta, option.ctaText || fallback);
    }

    const syncFloating =
      root.getAttribute('data-imbb-sticky-sync-option') === 'true';

    if (syncFloating && floatingSummary) {
      const fallbackSummary =
        floatingSummary.getAttribute('data-imbb-default-summary') || '';

      floatingSummary.textContent =
        option.mobileSummary || fallbackSummary;
    }

    if (syncFloating && floatingSub) {
      const fallbackSub =
        floatingSub.getAttribute('data-imbb-default-sub') || '';

      const selectedPerServing = option.card
        ? String(
            option.card.querySelector('.imbb__perServing')?.textContent || ''
          ).trim()
        : '';

      if (selectedPerServing) {
        floatingSub.textContent = selectedPerServing;
        floatingSub.hidden = false;
      } else if (!option.key && fallbackSub) {
        floatingSub.textContent = fallbackSub;
        floatingSub.hidden = false;
      } else {
        floatingSub.textContent = '';
        floatingSub.hidden = true;
      }
    }

    if (syncFloating && floatingCta) {
      const fallback =
        floatingCta.getAttribute('data-imbb-default-label') || '';

      setButtonText(
        floatingCta,
        option.mobileCta || option.ctaText || fallback
      );
    }
  }

  function dispatchSelectionChange(root) {
    let mainItems = [];
    let freebies = [];

    try {
      mainItems = buildMainItems(root);
      freebies = buildFreebieItems(root);
    } catch (error) {
      // Do not break option/flavor selection because an advanced checkout
      // configuration is still being edited in the Theme Editor.
    }

    document.dispatchEvent(
      new CustomEvent('im-buy-box:selection-change', {
        detail: {
          root,
          sectionId: root.getAttribute('data-imbb-section-id') || '',
          option: getSelectedOption(root),
          flavor: getSelectedFlavor(root),
          mainItems,
          freebies,
        },
      })
    );
  }

  function bindSelections(root) {
    if (!root || root.__imbbSelectionsBound) return;
    root.__imbbSelectionsBound = true;

    ensureDefaultSelections(root);
    syncFlavorUi(root, { skipGallery: true });
    syncPurchaseUi(root);

    root.querySelectorAll('[data-imbb-purchase-input]').forEach((input) => {
      const card = input.closest('[data-imbb-purchase-card]');

      input.addEventListener('change', () => {
        if (!input.checked) return;

        syncPurchaseUi(root);
        dispatchSelectionChange(root);

        const directCheckout =
          card &&
          card.getAttribute('data-imbb-direct-checkout') === 'true';

        if (directCheckout) {
          handleCheckoutClick(input);
        }
      });

      if (card) {
        card.addEventListener('click', () => {
          const directCheckout =
            card.getAttribute('data-imbb-direct-checkout') === 'true';

          if (
            directCheckout &&
            input.checked &&
            !root.__imbbCheckoutSubmitting
          ) {
            window.setTimeout(() => {
              if (!root.__imbbCheckoutSubmitting) {
                handleCheckoutClick(input);
              }
            }, 0);
          }
        });
      }
    });

    root.querySelectorAll('[data-imbb-flavor-input]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        syncFlavorUi(root);
        syncPurchaseUi(root);
        dispatchSelectionChange(root);
      });
    });

    const quickSummary = root.querySelector('[data-imbb-scroll-purchases]');
    if (quickSummary) {
      quickSummary.addEventListener('click', () => {
        const purchases = root.querySelector('[data-imbb-purchases]');
        smoothScrollToTarget(purchases);
      });
    }
  }

  function getGalleryState(root) {
    if (!root) return null;

    if (!root.__imbbGalleryState) {
      root.__imbbGalleryState = {
        index: 0,
        slides: Array.from(root.querySelectorAll('[data-imbb-gallery-slide]')),
        thumbs: Array.from(root.querySelectorAll('[data-imbb-thumb]')),
        prev: root.querySelector('[data-imbb-gallery-nav="prev"]'),
        next: root.querySelector('[data-imbb-gallery-nav="next"]'),
      };
    }

    return root.__imbbGalleryState;
  }

  function syncCliniciansChoiceSticker(root, state) {
    if (!root || !state || !state.slides.length) return;

    const sticker = root.querySelector(
      '[data-imbb-clinicians-sticker]'
    );

    if (!sticker) return;

    const activeSlide = state.slides[state.index] || null;

    const shouldShow =
      !!activeSlide &&
      activeSlide.getAttribute(
        'data-imbb-clinicians-sticker'
      ) === '1';

    sticker.classList.toggle(
      'is-hidden',
      !shouldShow
    );

    sticker.setAttribute(
      'aria-hidden',
      shouldShow ? 'false' : 'true'
    );
  }

  function setGalleryIndex(root, nextIndex) {
    const state = getGalleryState(root);
    if (!state || !state.slides.length) return;

    const max = state.slides.length - 1;
    state.index = Math.min(
      Math.max(Number(nextIndex) || 0, 0),
      max
    );

    state.slides.forEach((slide, index) => {
      const active = index === state.index;

      if (active) {
        slide.setAttribute(
          'data-imbb-active',
          '1'
        );
      } else {
        slide.removeAttribute(
          'data-imbb-active'
        );
      }

      slide.setAttribute(
        'aria-hidden',
        active ? 'false' : 'true'
      );
    });

    state.thumbs.forEach((thumb, index) => {
      const active = index === state.index;

      if (active) {
        thumb.setAttribute(
          'data-imbb-active',
          '1'
        );
      } else {
        thumb.removeAttribute(
          'data-imbb-active'
        );
      }

      thumb.setAttribute(
        'aria-selected',
        active ? 'true' : 'false'
      );
    });

    if (state.prev) {
      state.prev.disabled =
        state.index <= 0;
    }

    if (state.next) {
      state.next.disabled =
        state.index >= max;
    }

    syncCliniciansChoiceSticker(
      root,
      state
    );

    const activeThumb =
      state.thumbs[state.index];

    if (
      activeThumb &&
      typeof activeThumb.scrollIntoView === 'function'
    ) {
      activeThumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  function bindGallery(root) {
    if (!root || root.__imbbGalleryBound) return;
    root.__imbbGalleryBound = true;

    const state = getGalleryState(root);
    if (!state || !state.slides.length) return;

    const activeIndex = state.slides.findIndex(
      (slide) => slide.getAttribute('data-imbb-active') === '1'
    );
    state.index = activeIndex >= 0 ? activeIndex : 0;

    state.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        setGalleryIndex(
          root,
          Number(thumb.getAttribute('data-imbb-gallery-index')) || 0
        );
      });
    });

    if (state.prev) {
      state.prev.addEventListener('click', () => {
        setGalleryIndex(root, state.index - 1);
      });
    }

    if (state.next) {
      state.next.addEventListener('click', () => {
        setGalleryIndex(root, state.index + 1);
      });
    }

    const galleryMain = root.querySelector('[data-imbb-gallery-main]');
    if (galleryMain && state.slides.length > 1) {
      let pointerStartX = null;
      let pointerStartY = null;

      galleryMain.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      });

      galleryMain.addEventListener('pointerup', (event) => {
        if (pointerStartX === null || pointerStartY === null) return;

        const dx = event.clientX - pointerStartX;
        const dy = event.clientY - pointerStartY;
        pointerStartX = null;
        pointerStartY = null;

        if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy)) return;
        setGalleryIndex(root, dx < 0 ? state.index + 1 : state.index - 1);
      });

      galleryMain.addEventListener('pointercancel', () => {
        pointerStartX = null;
        pointerStartY = null;
      });
    }

    setGalleryIndex(root, state.index);
  }

  function bindAssistant(root) {
    if (!root || root.__imbbAssistantBound) return;
    root.__imbbAssistantBound = true;

    const tabs = Array.from(root.querySelectorAll('[data-imbb-assistant-tab]'));
    const panels = Array.from(
      root.querySelectorAll('[data-imbb-assistant-panel]')
    );

    const activateTab = (key) => {
      tabs.forEach((tab) => {
        const active = tab.getAttribute('data-imbb-assistant-tab') === key;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const active = panel.getAttribute('data-imbb-assistant-panel') === key;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        activateTab(tab.getAttribute('data-imbb-assistant-tab') || '');
      });
    });

    root.querySelectorAll('[data-imbb-assistant-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const input = form.querySelector('input[name="question"]');
        const responseEl = form.parentElement
          ? form.parentElement.querySelector('[data-imbb-assistant-response]')
          : null;
        const submit = form.querySelector('button[type="submit"]');
        const question = input ? input.value.trim() : '';
        const endpoint = form.getAttribute('data-imbb-assistant-endpoint') || '';
        const tabKey = form.getAttribute('data-imbb-assistant-tab-key') || '';

        if (!question) return;

        const setResponse = (value) => {
          if (!responseEl) return;
          responseEl.textContent = value == null ? '' : String(value);
        };

        if (!endpoint) {
          document.dispatchEvent(
            new CustomEvent('im-buy-box:assistant-submit', {
              detail: {
                root,
                sectionId: root.getAttribute('data-imbb-section-id') || '',
                tabKey,
                question,
                setResponse,
              },
            })
          );
          return;
        }

        if (submit) submit.disabled = true;

        try {
          const httpResponse = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              question,
              tab_key: tabKey,
              section_id: root.getAttribute('data-imbb-section-id') || '',
            }),
            credentials: 'same-origin',
          });

          const contentType = httpResponse.headers.get('content-type') || '';
          const data = contentType.includes('application/json')
            ? await httpResponse.json()
            : await httpResponse.text();

          if (!httpResponse.ok) {
            throw new Error(
              (data && (data.message || data.error)) || 'Unable to get an answer.'
            );
          }

          const answer =
            typeof data === 'string'
              ? data
              : data.answer || data.response || data.message || '';
          setResponse(answer);
        } catch (error) {
          setResponse(
            error && error.message ? error.message : 'Unable to get an answer.'
          );
        } finally {
          if (submit) submit.disabled = false;
        }
      });
    });
  }

  function bindAccordions(root) {
    if (!root || root.__imbbAccordionsBound) return;
    root.__imbbAccordionsBound = true;

    const accordions = Array.from(root.querySelectorAll('[data-imbb-accordion]'));
    const singleOpen = root.getAttribute('data-imbb-accordion-single') === 'true';

    accordions.forEach((accordion) => {
      const button = accordion.querySelector('[data-imbb-accordion-btn]');
      const panel = accordion.querySelector('[data-imbb-accordion-panel]');
      if (!button || !panel) return;

      button.addEventListener('click', () => {
        const willOpen = !accordion.classList.contains('is-open');

        if (singleOpen && willOpen) {
          accordions.forEach((other) => {
            if (other === accordion) return;
            other.classList.remove('is-open');
            const otherButton = other.querySelector('[data-imbb-accordion-btn]');
            const otherPanel = other.querySelector('[data-imbb-accordion-panel]');
            if (otherButton) otherButton.setAttribute('aria-expanded', 'false');
            if (otherPanel) otherPanel.hidden = true;
          });
        }

        accordion.classList.toggle('is-open', willOpen);
        button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        panel.hidden = !willOpen;
      });
    });
  }

  function getScrollOffsetPx() {
    try {
      const header = document.querySelector('.header-wrapper');
      if (!header) return 0;

      const style = window.getComputedStyle(header);
      if (style.display === 'none' || style.visibility === 'hidden') return 0;

      const position = String(style.position || '').toLowerCase();
      if (position !== 'fixed' && position !== 'sticky') return 0;

      return Math.max(0, Math.round(header.getBoundingClientRect().height || 0));
    } catch (error) {
      return 0;
    }
  }

  function smoothScrollToTarget(target) {
    if (!target) return;

    const offset = getScrollOffsetPx();
    const rect = target.getBoundingClientRect();
    const y = Math.round(window.pageYOffset + rect.top - offset - 12);

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  }

  function bindSmoothLinks(root) {
    if (!root || root.__imbbSmoothLinksBound) return;
    root.__imbbSmoothLinksBound = true;

    root.addEventListener('click', (event) => {
      const anchor = event.target && event.target.closest
        ? event.target.closest('a[href^="#"]')
        : null;
      if (!anchor || !root.contains(anchor)) return;

      const href = (anchor.getAttribute('href') || '').trim();
      if (!href || href === '#') return;

      const target = document.getElementById(href.slice(1));
      if (!target) return;

      event.preventDefault();
      smoothScrollToTarget(target);

      try {
        history.pushState(null, '', href);
      } catch (error) {}
    });
  }

  function bindNutritionModal(root) {
    if (!root || root.__imbbModalBound) return;
    root.__imbbModalBound = true;

    const modals = Array.from(
      root.querySelectorAll(
        '[data-imbb-nutrition-modal]'
      )
    );

    const openButtons = Array.from(
      root.querySelectorAll(
        '[data-imbb-nutrition-open]'
      )
    );

    if (
      !modals.length ||
      !openButtons.length
    ) {
      return;
    }

    let activeModal = null;
    let lastFocus = null;

    const getTargetModal = (button) => {
      const targetId = (
        button.getAttribute('aria-controls') ||
        button.getAttribute(
          'data-imbb-nutrition-target'
        ) ||
        ''
      ).trim();

      if (!targetId) {
        return (
          modals.find((modal) => !modal.id) ||
          modals[0] ||
          null
        );
      }

      const target =
        document.getElementById(
          targetId
        );

      if (
        !target ||
        !root.contains(target) ||
        !target.matches(
          '[data-imbb-nutrition-modal]'
        )
      ) {
        return null;
      }

      return target;
    };

    const stopModalVideos = (modal) => {
      if (!modal) return;

      modal
        .querySelectorAll('video')
        .forEach((video) => {
          try {
            video.pause();
            video.currentTime = 0;
          } catch (error) {}
        });
    };

    const playModalVideo = (modal) => {
      if (!modal) return;

      const video =
        modal.querySelector('video');

      if (!video) return;

      try {
        video.currentTime = 0;
        const playPromise = video.play();

        if (
          playPromise &&
          typeof playPromise.catch ===
            'function'
        ) {
          playPromise.catch(() => {});
        }
      } catch (error) {}
    };

    const closeModal = (
      modal = activeModal,
      restoreFocus = true
    ) => {
      if (!modal) return;

      stopModalVideos(modal);
      modal.hidden = true;

      if (activeModal === modal) {
        activeModal = null;
      }

      const hasOpenModal =
        modals.some(
          (item) => !item.hidden
        );

      if (!hasOpenModal) {
        document.documentElement.classList.remove(
          'imbb-modal-open'
        );
      }

      if (
        restoreFocus &&
        lastFocus &&
        typeof lastFocus.focus ===
          'function'
      ) {
        lastFocus.focus();
      }
    };

    const openModal = (button) => {
      const modal =
        getTargetModal(button);

      if (!modal) return;

      if (
        activeModal &&
        activeModal !== modal
      ) {
        closeModal(
          activeModal,
          false
        );
      }

      lastFocus = button;
      activeModal = modal;

      const gallery =
        modal.querySelector(
          '[data-imbb-nutrition-gallery]'
        );

      if (
        gallery &&
        typeof gallery.__imbbShowSlide ===
          'function'
      ) {
        gallery.__imbbShowSlide(0);
      }

      modal.hidden = false;

      document.documentElement.classList.add(
        'imbb-modal-open'
      );

      const close =
        modal.querySelector(
          '[data-imbb-nutrition-close]'
        );

      if (
        close &&
        typeof close.focus ===
          'function'
      ) {
        close.focus();
      }

      playModalVideo(modal);
    };

    openButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          openModal(button);
        }
      );
    });

    modals.forEach((modal) => {
      const gallery =
        modal.querySelector(
          '[data-imbb-nutrition-gallery]'
        );

      if (gallery) {
        const slides = Array.from(
          gallery.querySelectorAll(
            '[data-imbb-nutrition-slide]'
          )
        );

        let galleryIndex = 0;
        let requestedIndex = 0;
        let slideRequestId = 0;

        const normalizeSlideIndex = (index) =>
          ((Number(index) % slides.length) +
            slides.length) %
          slides.length;

        const ensureSlideReady = (slide) => {
          if (!slide) return Promise.resolve();

          if (slide.__imbbReadyPromise) {
            return slide.__imbbReadyPromise;
          }

          const image =
            slide.querySelector(
              '.imbb__nutritionModalImg'
            );

          if (!image) {
            slide.__imbbReadyPromise =
              Promise.resolve();

            return slide.__imbbReadyPromise;
          }

          image.loading = 'eager';

          const waitForLoad =
            image.complete
              ? Promise.resolve()
              : new Promise((resolve) => {
                  const finish = () => {
                    image.removeEventListener(
                      'load',
                      finish
                    );

                    image.removeEventListener(
                      'error',
                      finish
                    );

                    resolve();
                  };

                  image.addEventListener(
                    'load',
                    finish
                  );

                  image.addEventListener(
                    'error',
                    finish
                  );

                  if (image.complete) {
                    finish();
                  }
                });

          slide.__imbbReadyPromise =
            waitForLoad.then(() => {
              if (
                !image.naturalWidth ||
                typeof image.decode !==
                  'function'
              ) {
                return;
              }

              return image
                .decode()
                .catch(() => {});
            });

          return slide.__imbbReadyPromise;
        };

        const renderSlide = (nextIndex) => {
          if (!slides.length) return;

          galleryIndex =
            normalizeSlideIndex(nextIndex);

          requestedIndex = galleryIndex;

          slides.forEach(
            (slide, slideIndex) => {
              const isActive =
                slideIndex ===
                galleryIndex;

              slide.hidden = !isActive;

              slide.setAttribute(
                'aria-hidden',
                isActive
                  ? 'false'
                  : 'true'
              );
            }
          );

          gallery
            .querySelectorAll(
              '[data-imbb-nutrition-gallery-nav]'
            )
            .forEach((button) => {
              button.hidden =
                slides.length <= 1;
            });
        };

        const showSlide = (nextIndex) => {
          if (!slides.length) {
            return Promise.resolve();
          }

          requestedIndex =
            normalizeSlideIndex(nextIndex);

          const targetIndex =
            requestedIndex;

          const requestId =
            ++slideRequestId;

          return ensureSlideReady(
            slides[targetIndex]
          ).then(() => {
            if (
              requestId !==
              slideRequestId
            ) {
              return;
            }

            renderSlide(targetIndex);
          });
        };

        const moveSlide = (direction) => {
          showSlide(
            requestedIndex + direction
          );
        };

        gallery.__imbbShowSlide =
          showSlide;

        gallery.__imbbMoveSlide =
          moveSlide;

        gallery
          .querySelectorAll(
            '[data-imbb-nutrition-gallery-nav]'
          )
          .forEach((button) => {
            button.addEventListener(
              'click',
              () => {
                moveSlide(
                  button.getAttribute(
                    'data-imbb-nutrition-gallery-nav'
                  ) === 'prev'
                    ? -1
                    : 1
                );
              }
            );
          });

        renderSlide(0);

        slides.forEach((slide) => {
          ensureSlideReady(slide);
        });
      }

      modal
        .querySelectorAll(
          '[data-imbb-nutrition-close]'
        )
        .forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              closeModal(modal);
            }
          );
        });
    });

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          !activeModal ||
          activeModal.hidden
        ) {
          return;
        }

        if (event.key === 'Escape') {
          closeModal(activeModal);
          return;
        }

        const gallery =
          activeModal.querySelector(
            '[data-imbb-nutrition-gallery]'
          );

        if (
          !gallery ||
          typeof gallery.__imbbMoveSlide !==
            'function'
        ) {
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          gallery.__imbbMoveSlide(-1);
        } else if (
          event.key === 'ArrowRight'
        ) {
          event.preventDefault();
          gallery.__imbbMoveSlide(1);
        }
      }
    );
  }

  function setStickyVisible(root, visible) {
    if (!root) return;

    const sticky = root.querySelector(STICKY_SELECTOR);
    if (!sticky) return;

    root.classList.toggle('imbb-sticky-active', !!visible);
    sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function createStickyFlowers(root) {
    const wrap = root && root.querySelector('[data-imbb-sticky-flowers]');
    if (!wrap || wrap.hasChildNodes()) return;

    const isMobile = window.matchMedia('(max-width: 899px)').matches;
    const count = isMobile ? 16 : 28;
    const colors = [
      'is-pink',
      'is-yellow',
      'is-purple',
      'is-blue',
      'is-mint',
      'is-orange',
    ];

    for (let index = 0; index < count; index += 1) {
      const flower = document.createElement('span');
      const useLeft = index % 2 === 0;
      const left = useLeft ? Math.random() * 30 : 70 + Math.random() * 30;
      const size = isMobile ? 9 + Math.random() * 7 : 11 + Math.random() * 10;
      const drift = -18 + Math.random() * 36;
      const rotation = Math.random() > 0.5 ? 360 : -360;

      flower.className = `imbb__stickyFlower ${colors[index % colors.length]}`;
      flower.style.left = `${left.toFixed(2)}%`;
      flower.style.setProperty('--imbb-sticky-flower-size', `${size.toFixed(2)}px`);
      flower.style.setProperty(
        '--imbb-sticky-flower-duration',
        `${(4.5 + Math.random() * 4).toFixed(2)}s`
      );
      flower.style.setProperty(
        '--imbb-sticky-flower-delay',
        `${(-Math.random() * 9).toFixed(2)}s`
      );
      flower.style.setProperty('--imbb-sticky-flower-drift-x', `${drift.toFixed(2)}px`);
      flower.style.setProperty('--imbb-sticky-flower-rotation', `${rotation}deg`);
      flower.style.setProperty(
        '--imbb-sticky-flower-static-top',
        `${(12 + Math.random() * 76).toFixed(2)}%`
      );
      flower.style.setProperty(
        '--imbb-sticky-flower-static-rotation',
        `${(-30 + Math.random() * 60).toFixed(2)}deg`
      );

      wrap.appendChild(flower);
    }
  }

  function bindSticky(root) {
    if (!root || root.__imbbStickyBound) return;

    const sticky = root.querySelector(STICKY_SELECTOR);
    const mainCta = root.querySelector('[data-imbb-main-cta]');
    if (!sticky || !mainCta) return;

    root.__imbbStickyBound = true;
    createStickyFlowers(root);

    const triggerMode =
      sticky.getAttribute('data-imbb-sticky-trigger') || 'main_cta_out_of_view';

    const update = () => {
      if (!document.documentElement.contains(root)) return;

      if (triggerMode === 'below_top_shop') {
        const rect = root.getBoundingClientRect();
        setStickyVisible(root, rect.bottom <= window.innerHeight);
        return;
      }

      const rect = mainCta.getBoundingClientRect();

      /*
       * Only reveal after the shopper has actually scrolled past
       * the main CTA. Do not show it merely because the CTA is still
       * farther down the page.
       */
      setStickyVisible(root, rect.bottom <= 0);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function syncThemeChrome() {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));

    document.documentElement.classList.toggle(
      'imbb-hide-theme-header',
      roots.some((root) => root.getAttribute('data-imbb-hide-header') === 'true')
    );
    document.documentElement.classList.toggle(
      'imbb-hide-announcement',
      roots.some(
        (root) => root.getAttribute('data-imbb-hide-announcement') === 'true'
      )
    );
    document.documentElement.classList.toggle(
      'imbb-hide-progress',
      roots.some(
        (root) => root.getAttribute('data-imbb-hide-progress') === 'true'
      )
    );
  }

  function getSyncRole(root) {
    return root
      ? (root.getAttribute('data-imbb-sync-role') || 'standalone').trim()
      : 'standalone';
  }

  function getSyncGroup(root) {
    return root
      ? (root.getAttribute('data-imbb-sync-group') || '').trim()
      : '';
  }

  function rewriteSyncedReferences(root, sourceSectionId, targetSectionId) {
    if (
      !root ||
      !sourceSectionId ||
      !targetSectionId ||
      sourceSectionId === targetSectionId
    ) {
      return;
    }

    const idMap = new Map();

    root.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      if (!oldId || !oldId.includes(sourceSectionId)) return;
      const newId = oldId.split(sourceSectionId).join(targetSectionId);
      element.id = newId;
      idMap.set(oldId, newId);
    });

    root.querySelectorAll('*').forEach((element) => {
      ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for'].forEach(
        (attributeName) => {
          const raw = element.getAttribute(attributeName);
          if (!raw) return;

          const next = raw
            .split(/\s+/)
            .map((value) => idMap.get(value) || value)
            .join(' ');
          if (next !== raw) element.setAttribute(attributeName, next);
        }
      );

      const href = element.getAttribute('href');
      if (href && href.charAt(0) === '#') {
        const id = href.slice(1);
        if (idMap.has(id)) element.setAttribute('href', `#${idMap.get(id)}`);
      }

      const name = element.getAttribute('name');
      if (name && name.includes(sourceSectionId)) {
        element.setAttribute(
          'name',
          name.split(sourceSectionId).join(targetSectionId)
        );
      }
    });
  }

  function removeCopiedEditorAttributes(root) {
    if (!root) return;
    root.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
      element.removeAttribute('data-shopify-editor-block');
    });
  }

  function revealSyncCopy(root) {
    if (!root) return;
    root.style.removeProperty('visibility');
    root.setAttribute('data-imbb-sync-ready', 'true');
  }

  function copyRootAttributes(source, target) {
    const targetScrollId = target.getAttribute('id') || '';
    const preserved = new Map([
      ['data-imbb-section-id', target.getAttribute('data-imbb-section-id') || ''],
      ['data-imbb-sync-role', target.getAttribute('data-imbb-sync-role') || 'copy'],
      ['data-imbb-sync-group', target.getAttribute('data-imbb-sync-group') || ''],
      ['data-imbb-sync-ready', target.getAttribute('data-imbb-sync-ready') || 'false'],
    ]);

    Array.from(target.attributes).forEach((attribute) => {
      target.removeAttribute(attribute.name);
    });

    Array.from(source.attributes).forEach((attribute) => {
      target.setAttribute(attribute.name, attribute.value);
    });

    preserved.forEach((value, name) => target.setAttribute(name, value));

    if (targetScrollId) target.setAttribute('id', targetScrollId);
    else target.removeAttribute('id');
  }

  function syncRootFromSource(source, target) {
    if (!source || !target || source === target) return;

    const sourceSectionId = source.getAttribute('data-imbb-section-id') || '';
    const targetSectionId = target.getAttribute('data-imbb-section-id') || '';

    copyRootAttributes(source, target);
    target.classList.remove('imbb-sticky-active');
    target.innerHTML = source.innerHTML;

    rewriteSyncedReferences(target, sourceSectionId, targetSectionId);
    removeCopiedEditorAttributes(target);

    const copiedSticky = target.querySelector(STICKY_SELECTOR);
    if (copiedSticky) copiedSticky.remove();

    target.__imbbBound = false;
    target.__imbbSelectionsBound = false;
    target.__imbbGalleryBound = false;
    target.__imbbGalleryState = null;
    target.__imbbAssistantBound = false;
    target.__imbbAccordionsBound = false;
    target.__imbbSmoothLinksBound = false;
    target.__imbbModalBound = false;
    target.__imbbStickyBound = false;
    target.__imbbParsedConfig = null;
    target.__imbbCheckoutSubmitting = false;

    revealSyncCopy(target);
  }

  function syncBuyBoxCopies() {
    const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));
    const sourcesByGroup = new Map();

    roots.forEach((root) => {
      if (getSyncRole(root) !== 'source') return;
      const group = getSyncGroup(root);
      if (!group) return;

      if (sourcesByGroup.has(group)) {
        console.warn(
          `IM Buy Box found more than one Master in sync group "${group}". The first Master is used.`
        );
        return;
      }

      sourcesByGroup.set(group, root);
    });

    roots.forEach((root) => {
      if (getSyncRole(root) !== 'copy') return;

      const group = getSyncGroup(root);
      const source = group ? sourcesByGroup.get(group) : null;

      if (!source) {
        revealSyncCopy(root);

        if (!root.__imbbMissingSyncSourceWarned) {
          console.warn(
            `IM Buy Box Copy could not find a Master in sync group "${
              group || '(blank)'
            }". Its own section settings are used instead.`
          );
          root.__imbbMissingSyncSourceWarned = true;
        }
        return;
      }

      syncRootFromSource(source, root);
    });
  }

  function bindRoot(root) {
    if (!root || root.__imbbBound) return;

    const reloadState = getBogoReloadState();
    const rootSectionId =
      root.getAttribute('data-imbb-section-id') || '';

    const reloadMatchesRoot =
      !!reloadState &&
      (
        !reloadState.sectionId ||
        reloadState.sectionId === rootSectionId
      );

    const shouldResumeCheckout =
      reloadMatchesRoot &&
      reloadState.mode === 'checkout';

    if (reloadMatchesRoot) {
      restoreBogoReloadSelection(root, reloadState);
    }

    stripInternalParamsFromCurrentUrl();

    root.__imbbBound = true;

    bindSelections(root);
    bindGallery(root);
    bindAssistant(root);
    bindAccordions(root);
    bindSmoothLinks(root);
    bindNutritionModal(root);

    root.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      button.addEventListener('click', () => handleCheckoutClick(button));
    });

    if (getSyncRole(root) !== 'copy') bindSticky(root);

    if (shouldResumeCheckout) {
      restoreBogoReloadScroll(reloadState);
      setBusy(root, true);

      const resumeButton =
        root.querySelector('[data-imbb-main-cta]') ||
        root.querySelector(CHECKOUT_SELECTOR);

      if (resumeButton) {
        window.requestAnimationFrame(() => {
          restoreBogoReloadScroll(reloadState);

          window.setTimeout(() => {
            if (!root.__imbbCheckoutSubmitting) {
              handleCheckoutClick(resumeButton);
            }
          }, 30);
        });
      }
    }
  }

  function init() {
    syncBuyBoxCopies();
    syncThemeChrome();
    document.querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', () => {
    window.setTimeout(syncThemeChrome, 0);
  });

  if (document.readyState !== 'loading') init();
})();
